/**
 * This World's spaces (RFC 0281 §7), live.
 *
 * Two pointers deep: the World pointer names the World, and the World names its
 * spaces document (`spacesDocId`, the split RFC 0261 made for presence). Each
 * hop can be *not here yet* rather than *not there*, which is why `loaded` is
 * reported separately from an empty list — §8's invariant, and the reason
 * `resolveSpace` takes it as a parameter instead of inferring it.
 *
 * `useDocument(undefined)` is legal and yields nothing, so both hops run
 * unconditionally and the rules of hooks are satisfied without branching.
 */

import type { AnyDocumentId } from '@automerge/automerge-repo/slim'
import {
  projectSpaces,
  spacesDocId as readSpacesDocId,
  type SpaceRef,
} from '@mod/core/model/spaces'
import { useMemo } from 'react'
import { useDocument } from '../../services/world/repo/useDocument'
import { readWorldPointer } from '../../services/world/world'

export interface SpacesView {
  spaces: SpaceRef[]
  /**
   * Has the spaces document actually been READ?
   *
   * False while either hop is outstanding, and the difference that matters:
   * `spaces: []` with `loaded: false` means "wait", and with `loaded: true`
   * means "this World genuinely has none, so make one".
   */
  loaded: boolean
  /** The spaces document's id, for the writes. Null until the World says. */
  spacesDocId: string | null
  /** This window's World, so a caller can create the spaces doc and link it. */
  worldId: string | null
}

export function useSpaces(): SpacesView {
  const worldId = readWorldPointer()
  const [worldDoc] = useDocument(worldId as AnyDocumentId | undefined)
  const docId = useMemo(() => readSpacesDocId(worldDoc), [worldDoc])
  const [spacesDoc] = useDocument(docId as AnyDocumentId | undefined)

  return useMemo(
    () => ({
      spaces: projectSpaces(spacesDoc),
      // A World with no `spacesDocId` at all IS loaded once the World itself
      // is: there is no second document to wait for, and the honest answer is
      // "no spaces yet", which is what makes the first one get created.
      loaded: docId ? spacesDoc !== undefined : worldDoc !== undefined,
      spacesDocId: docId,
      worldId,
    }),
    [spacesDoc, docId, worldDoc, worldId],
  )
}
